import { useState, useEffect, useMemo } from 'react'
import { lockScroll, unlockScroll } from '../../lib/scrollLock'
import { calculateRiskPoints, calculateRewardPoints } from '../../lib/scoring'
import { useKeyboardOpen } from '../../hooks/useKeyboardOpen'

function teamName(fullName) {
  if (!fullName) return ''
  const parts = fullName.split(' ')
  return parts[parts.length - 1]
}

// A prop pick still counts toward the bar only while its game is in the
// future. Module scope keeps the Date.now() call out of render.
function isStillPickable(pick) {
  const start = pick?.player_props?.games?.starts_at
  return !!start && new Date(start).getTime() > Date.now()
}

export default function BottomBar({ picks, games, propPicks, profile, onUpdateMultiplier }) {
  const [expanded, setExpanded] = useState(false)
  // Hidden entirely while the user is typing.
  //
  // The bar is pinned to the bottom of the viewport, and with the Keyboard
  // plugin on `resize: "native"` that bottom MOVES to just above the
  // keyboard — so searching for a player left the summary parked on top of
  // the results, which is both ugly and in the way of the thing being
  // searched for. --kb-inset cannot push it back down because native resize
  // makes that figure 0.
  //
  // Nothing is lost by hiding it: the bar reports a running total, and the
  // moment the keyboard closes it is back with the same numbers.
  const keyboardOpen = useKeyboardOpen()
  const [multiplyOn, setMultiplyOn] = useState(false)
  const [tappedGameId, setTappedGameId] = useState(null)

  // Lock body scroll when expanded on mobile
  useEffect(() => {
    if (expanded) {
      lockScroll()
      return () => unlockScroll()
    }
  }, [expanded])

  const isOnboarding = profile?.has_seen_onboarding === false
  const entries = Object.entries(picks || {})
  // Pending prop picks whose game HASN'T STARTED yet.
  //
  // This used to filter to same-day, which worked only while props were
  // same-day. Football props publish days ahead now, so a Thursday pick made
  // on Saturday vanished from the bar entirely.
  //
  // Not-yet-started is the right scope rather than simply 'pending': a prop
  // pick that never got locked stays 'pending' forever even after its game
  // finishes, so dropping the date filter alone resurfaced a LeBron pick
  // from February. Same guard PropsSection uses to decide what's pickable.
  const propEntries = useMemo(() => (propPicks || []).filter(isStillPickable), [propPicks])

  // After every hook — an early return above useMemo would change the hook
  // order between renders, which React treats as an error.
  if (keyboardOpen) return null

  // During onboarding, show a placeholder so the tutorial can highlight this element
  if (entries.length === 0 && propEntries.length === 0) {
    if (!isOnboarding) return null
    return (
      <div
        data-onboarding="bottom-bar"
        className="fixed bottom-[calc(3.5rem+env(safe-area-inset-bottom))] xl:bottom-0 translate-y-[var(--kb-inset,0px)] left-0 right-0 bg-bg-secondary border-t border-border px-4 py-3 z-40"
      >
        <div className="max-w-2xl mx-auto flex items-center gap-2">
          <div className="flex-1 text-sm text-text-muted text-center">
            Your picks will appear here
          </div>
        </div>
      </div>
    )
  }

  const totalPoints = profile?.total_points ?? 0
  const canMultiply = totalPoints >= 20

  // Calculate used budget from other multiplied picks
  const usedBudget = entries.reduce((sum, [, pick]) => {
    if (!pick.multiplier || pick.multiplier <= 1) return sum
    const game = games?.find((g) => g.id === pick.game_id)
    if (!game) return sum
    const odds = pick.picked_team === 'home' ? game.home_odds : game.away_odds
    if (!odds) return sum
    const baseRisk = calculateRiskPoints(odds)
    return sum + baseRisk * (pick.multiplier - 1)
  }, 0)

  const remainingBudget = totalPoints - usedBudget

  // Compute totals from straight picks
  let totalRisk = 0
  let totalReward = 0
  let favCount = 0
  let dogCount = 0

  for (const [, pick] of entries) {
    const game = games?.find((g) => g.id === pick.game_id)
    if (!game) continue
    const currentOdds = pick.picked_team === 'home' ? game.home_odds : game.away_odds
    const oddsForCalc = pick.odds_at_pick ?? currentOdds
    const mult = pick.multiplier || 1
    // risk_at_submission/reward_at_submission already include the multiplier
    const displayedRisk = pick.risk_at_submission ?? (oddsForCalc != null ? calculateRiskPoints(oddsForCalc) * mult : null)
    const displayedReward = pick.reward_at_submission ?? (oddsForCalc != null ? calculateRewardPoints(oddsForCalc) * mult : null)
    if (displayedRisk == null) continue
    totalRisk += displayedRisk
    totalReward += displayedReward
    if (oddsForCalc != null && oddsForCalc < 0) favCount++
    else dogCount++
  }

  // Add prop picks to totals
  for (const prop of propEntries) {
    const odds = prop.odds_at_submission
    if (!odds) continue
    totalRisk += prop.risk_at_submission || calculateRiskPoints(odds)
    totalReward += prop.reward_at_submission || calculateRewardPoints(odds)
    if (odds < 0) favCount++
    else dogCount++
  }

  const totalPickCount = favCount + dogCount
  if (totalPickCount === 0) return null

  const summaryBar = (
    <div className="flex items-center justify-between text-sm">
      <div className="flex items-center gap-4">
        <span className="text-text-primary font-semibold">{totalPickCount} pick{totalPickCount !== 1 ? 's' : ''}</span>
        <span className="text-text-muted">
          <span className="hidden md:inline">{favCount} {favCount === 1 ? 'Favorite' : 'Favorites'} / {dogCount} {dogCount === 1 ? 'Dog' : 'Dogs'}</span>
          <span className="md:hidden">{favCount} {favCount === 1 ? 'Fave' : 'Faves'} / {dogCount} {dogCount === 1 ? 'Dog' : 'Dogs'}</span>
        </span>
      </div>
      <div className="flex items-center gap-4">
        <span className="text-incorrect">Risk: {totalRisk}</span>
        <span className="text-correct">Reward: {totalReward}</span>
      </div>
    </div>
  )

  if (!expanded) {
    return (
      <div
        data-onboarding="bottom-bar"
        className="fixed bottom-[calc(3.5rem+env(safe-area-inset-bottom))] xl:bottom-0 translate-y-[var(--kb-inset,0px)] left-0 right-0 bg-bg-secondary border-t border-border px-4 py-3 z-40 cursor-pointer"
        onClick={() => setExpanded(true)}
      >
        <div className="max-w-2xl mx-auto flex items-center gap-2">
          <div className="flex-1">{summaryBar}</div>
          <svg className="w-5 h-5 text-text-muted flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
          </svg>
        </div>
      </div>
    )
  }

  return (
    <div className="fixed bottom-[calc(3.5rem+env(safe-area-inset-bottom))] xl:bottom-0 translate-y-[var(--kb-inset,0px)] left-0 right-0 bg-bg-secondary border-t border-border z-40">
      <div className="max-w-2xl mx-auto px-4 py-3">
        {/* Top row: Multiply toggle + close */}
        <div className="flex items-center justify-between mb-3">
          <button
            onClick={() => setMultiplyOn(!multiplyOn)}
            className={`flex items-center gap-2 text-sm font-semibold px-3 py-1.5 rounded-lg transition-colors ${
              multiplyOn && canMultiply
                ? 'bg-accent text-white'
                : 'bg-bg-card text-text-secondary border border-border'
            } ${!canMultiply ? 'opacity-50' : ''}`}
          >
            Multiply
          </button>
          <button onClick={() => setExpanded(false)} className="p-1 text-text-muted hover:text-text-primary">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
        </div>

        {/* Minimum points message */}
        {multiplyOn && !canMultiply && (
          <div className="text-xs text-text-muted mb-3 bg-bg-card rounded-lg px-3 py-2 border border-border">
            You need a minimum of 20 points to multiply your risk/reward. You're not quite there yet. Keep grinding!
          </div>
        )}

        {/* Pick list */}
        {/* overflow-x-hidden is load-bearing, not belt-and-braces. CSS says
            that when one overflow axis is not `visible`, the other computes
            to `auto` — so `overflow-y-auto` alone silently made this
            horizontally scrollable too. The straight-pick row below bleeds
            4px each side (`-mx-1`) to run its tap highlight to the edges,
            and that bleed became a 4px scroll range: drag sideways and every
            row shifted, clipping the first letter of each player's name.
            There is nothing to the side worth reaching, so pin the axis. */}
        <div className="space-y-2 max-h-60 overflow-y-auto overflow-x-hidden scrollbar-hide mb-3">
          {/* Straight picks */}
          {entries.map(([gameId, pick]) => {
            const game = games?.find((g) => g.id === gameId)
            if (!game) return null
            const currentOdds = pick.picked_team === 'home' ? game.home_odds : game.away_odds
            const oddsForCalc = pick.odds_at_pick ?? currentOdds
            const mult = pick.multiplier || 1

            // Base (single-x) risk/reward — used for affordability checks
            const baseRisk = oddsForCalc != null
              ? calculateRiskPoints(oddsForCalc)
              : (pick.risk_at_submission != null ? pick.risk_at_submission / mult : null)
            const baseReward = oddsForCalc != null
              ? calculateRewardPoints(oddsForCalc)
              : (pick.reward_at_submission != null ? pick.reward_at_submission / mult : null)

            // Displayed risk/reward — prefer locked submission values, which already include the multiplier
            const displayedRisk = pick.risk_at_submission ?? (baseRisk != null ? baseRisk * mult : null)
            const displayedReward = pick.reward_at_submission ?? (baseReward != null ? baseReward * mult : null)

            if (displayedRisk == null) return null

            // Determine which multiplier squares are affordable
            const affordableMultipliers = [2, 3, 4].filter((m) => {
              if (baseRisk == null) return false
              const extraCost = baseRisk * (m - 1)
              const currentExtra = mult > 1 ? baseRisk * (mult - 1) : 0
              return (extraCost - currentExtra) <= remainingBudget
            })

            return (
              <div key={gameId}>
                <div
                  className="flex items-center gap-3 py-1.5 cursor-pointer active:bg-white/5 rounded-lg -mx-1 px-1"
                  onClick={() => setTappedGameId(tappedGameId === gameId ? null : gameId)}
                >
                  {/* Team matchup */}
                  <div className="flex-1 min-w-0 text-sm truncate">
                    <span className={pick.picked_team === 'away' ? 'text-accent font-semibold' : 'text-text-primary'}>
                      {teamName(game.away_team)}
                    </span>
                    {/* "@" not "vs" — away is rendered on the left and home on
                        the right, so this reads "away @ home", matching the
                        picks list, Game Center and Game Intel. Hardcoded "vs"
                        told a road team's backer their game was at home. */}
                    <span className="text-text-muted"> @ </span>
                    <span className={pick.picked_team === 'home' ? 'text-accent font-semibold' : 'text-text-primary'}>
                      {teamName(game.home_team)}
                    </span>
                  </div>

                  {/* Multiplier squares */}
                  {multiplyOn && canMultiply && (
                    <div className="flex gap-1.5 flex-shrink-0">
                      {[2, 3, 4].map((m) => {
                        if (!affordableMultipliers.includes(m) && mult !== m) return null
                        const isActive = mult === m
                        return (
                          <button
                            key={m}
                            onClick={(e) => { e.stopPropagation(); onUpdateMultiplier(gameId, isActive ? 1 : m) }}
                            className={`w-8 h-8 rounded text-xs font-bold border transition-colors ${
                              isActive
                                ? 'bg-accent border-accent text-white'
                                : 'border-border text-text-secondary hover:border-accent hover:text-accent'
                            }`}
                          >
                            {m}x
                          </button>
                        )
                      })}
                    </div>
                  )}

                  {/* Risk → Reward */}
                  <div className="flex items-center gap-2 text-sm flex-shrink-0">
                    <span className="text-incorrect">{displayedRisk}</span>
                    <span className="text-text-muted">&rarr;</span>
                    <span className="text-correct">{displayedReward}</span>
                  </div>
                </div>
                {tappedGameId === gameId && (
                  <div className="bg-bg-card/80 rounded-lg px-3 py-2 mb-1 border border-text-primary/10">
                    {game.status === 'live' ? (
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-text-primary font-semibold">
                          {teamName(game.away_team)} {game.live_away_score ?? game.away_score ?? 0} @ {teamName(game.home_team)} {game.live_home_score ?? game.home_score ?? 0}
                        </span>
                        {game.period && <span className="text-accent font-semibold">Q{game.period} {game.clock}</span>}
                      </div>
                    ) : game.status === 'final' ? (
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-text-primary font-semibold">
                          {teamName(game.away_team)} {game.away_score ?? 0} @ {teamName(game.home_team)} {game.home_score ?? 0}
                        </span>
                        <span className="text-text-muted font-semibold">Final</span>
                      </div>
                    ) : (
                      <div className="text-sm text-text-primary text-center">
                        {new Date(game.starts_at).toLocaleString('en-US', { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )
          })}

          {/* Prop picks separator + list */}
          {propEntries.length > 0 && entries.length > 0 && (
            <div className="border-t border-border/50 my-1" />
          )}
          {propEntries.map((prop) => {
            const playerProp = prop.player_props
            const playerName = playerProp?.player_name || 'Prop'
            const line = playerProp?.line
            const market = playerProp?.market_label || playerProp?.market
            const risk = prop.risk_at_submission || 0
            const reward = prop.reward_at_submission || 0

            return (
              <div key={prop.id} className="flex items-center gap-3 py-1.5">
                <div className="flex-1 min-w-0 text-sm truncate">
                  <span className="text-accent font-semibold">{playerName}</span>
                  <span className="text-text-muted"> {prop.picked_side} {line} </span>
                  {market && <span className="text-text-muted text-xs">{market}</span>}
                </div>

                {/* No multiplier squares for props */}

                <div className="flex items-center gap-2 text-sm flex-shrink-0">
                  <span className="text-incorrect">{risk}</span>
                  <span className="text-text-muted">&rarr;</span>
                  <span className="text-correct">{reward}</span>
                </div>
              </div>
            )
          })}
        </div>

        {/* Bottom summary */}
        {summaryBar}
      </div>
    </div>
  )
}
